/**
 * The group colour palette and group-id minting (`specs/M1-B2-groups.md` §5.1, §5.2).
 *
 * The palette is the **23 hexes actually in use** across the 1,334 groups of this account,
 * ordered by frequency `[bj]`. It is deliberately *not* the 40-colour status palette: seven of
 * these (`#e2445c`, `#a25ddc`, `#0086c0`, `#ff642e`, `#ff158a`, `#808080`, `#ff007f`) do not
 * appear there at all, so the two lists are never shared (open question Q142).
 *
 * A colour outside the list is **stored, never rejected** — monday may add swatches and the
 * importer must be lossless — it is simply not offered by the picker.
 */
/** The 23 observed group colours, most frequent first (`specs/M1-B2-groups.md` §5.2). */
export const GROUP_COLORS = [
    '#579bfc',
    '#037f4c',
    '#a25ddc',
    '#00c875',
    '#0086c0',
    '#ffcb00',
    '#fdab3d',
    '#ff158a',
    '#e2445c',
    '#c4c4c4',
    '#784bd1',
    '#ff642e',
    '#66ccff',
    '#333333',
    '#ff5ac4',
    '#808080',
    '#cab641',
    '#9cd326',
    '#7f5347',
    '#bb3354',
    '#ff007f',
    '#757575',
    '#007eb5',
];
/** 303 of 1,334 groups use it, and it is the first swatch of the picker. */
export const DEFAULT_GROUP_COLOR = '#579bfc';
/** monday stores mixed case (`#784BD1` and `#784bd1` both occur); we normalise to lower. */
const HEX_COLOR = /^#[0-9a-f]{6}$/;
/**
 * Accepts any 7-character hex and lower-cases it; rejects `red`, `#fff` and `#GGGGGG`
 * (acceptance A7). Returns `null` when the input is not a hex colour at all.
 */
export function normalizeGroupColor(value) {
    if (typeof value !== 'string')
        return null;
    const lowered = value.trim().toLowerCase();
    return HEX_COLOR.test(lowered) ? lowered : null;
}
export function isPaletteGroupColor(value) {
    return GROUP_COLORS.includes(value.toLowerCase());
}
/**
 * "Add group" picks the next unused swatch in `GROUP_COLORS` order, cycling when all 23 are
 * taken (**ASSUMED**, open question Q139).
 */
export function nextGroupColor(used) {
    const taken = new Set();
    for (const value of used) {
        const normalized = normalizeGroupColor(value ?? null);
        if (normalized)
            taken.add(normalized);
    }
    return GROUP_COLORS.find((color) => !taken.has(color)) ?? DEFAULT_GROUP_COLOR;
}
/* ── ids ──────────────────────────────────────────────────────────────────── */
/**
 * Group ids are `[a-z0-9_]{1,64}`. monday's 20-character **column**-id rule does not apply:
 * real group ids reach 35 characters (`duplicate_of_requests_____othe`) `[bj]`.
 */
export const GROUP_ID_PATTERN = /^[a-z0-9_]{1,64}$/;
/** The longest observed title slug is 30 characters `[bj]`. */
export const MAX_GROUP_SLUG_LENGTH = 30;
/** 103 groups carry it; it is what "Add group" mints when the title slugifies to nothing. */
export const FALLBACK_GROUP_ID = 'new_group';
/** A duplicated group's id prefix (104 groups carry it) `[bj]`. */
export const DUPLICATE_GROUP_ID_PREFIX = 'duplicate_of_';
/** The first group of a fresh board `[repo:apps/api/src/services/boards.ts]`, `[bj]`. */
export const DEFAULT_GROUP_ID = 'topics';
/** Its title. 67 groups in the account still carry the untouched default `[bj]`. */
export const DEFAULT_GROUP_TITLE = 'Group Title';
/** "Add group" — inferred from the 103 groups whose id is `new_group` (**ASSUMED**, Q138). */
export const NEW_GROUP_TITLE = 'New Group';
export function isValidGroupId(id) {
    return GROUP_ID_PATTERN.test(id);
}
/** `"June - Tasks"` → `june___tasks`, exactly as monday slugifies it `[bj]`. */
export function slugifyGroupTitle(title) {
    const slug = title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, MAX_GROUP_SLUG_LENGTH);
    return slug === '' ? FALLBACK_GROUP_ID : slug;
}
function base36(value) {
    return Math.floor(value).toString(36);
}
/**
 * `specs/M1-B2-groups.md` §5.1. A free slug wins; otherwise the slug is truncated to 22
 * characters and suffixed with a base36 timestamp (**ASSUMED**, Q140).
 *
 * The importer never calls this: monday ids are inserted **verbatim**, `nuovo_gruppo68846` and
 * the 35-character ones included.
 */
export function mintGroupId(title, taken, now = Date.now) {
    const used = taken instanceof Set ? taken : new Set(taken);
    const slug = slugifyGroupTitle(title);
    if (!used.has(slug))
        return slug;
    const base = slug.slice(0, 22);
    for (let attempt = 0; attempt < 64; attempt += 1) {
        const candidate = `${base}_${base36(now() + attempt)}`;
        if (!used.has(candidate))
            return candidate;
    }
    throw new Error(`could not allocate a group id for "${title}"`);
}
/** `duplicate_of_<source id>` truncated at 30 characters, uniquified on collision (C4). */
export function duplicateGroupId(sourceId, taken) {
    const used = taken instanceof Set ? taken : new Set(taken);
    const base = `${DUPLICATE_GROUP_ID_PREFIX}${sourceId}`.slice(0, MAX_GROUP_SLUG_LENGTH);
    if (!used.has(base))
        return base;
    for (let suffix = 2; suffix < 1000; suffix += 1) {
        const tail = `_${suffix}`;
        const candidate = `${base.slice(0, MAX_GROUP_SLUG_LENGTH - tail.length)}${tail}`;
        if (!used.has(candidate))
            return candidate;
    }
    throw new Error(`could not allocate a duplicate group id for "${sourceId}"`);
}
/** `Duplicate of <title>` (**ASSUMED**, Q141 — only the `duplicate_of_` id form is observed). */
export function duplicateGroupTitle(title) {
    return `Duplicate of ${title}`;
}
//# sourceMappingURL=colors.js.map